import BlockchainProvider from './BlockchainProvider';
import ArchiveProvider from './ArchiveProvider';
import WizardProvider from './WizardProvider';

/**
 * Select a DataProvider based on configuration.
 *
 * Priority:
 *   1. URL param: ?source=archive or ?source=wizard
 *   2. Environment variable: REACT_APP_DATA_SOURCE
 *   3. Default: blockchain
 *
 * @returns {{ dataProvider: DataProvider, writeProvider: WriteProvider|null }}
 */
export function createDataProvider() {
  const source = getDataSource();

  switch (source) {
    case 'archive':
      return {
        dataProvider: new ArchiveProvider({
          networkId: getUrlParam('networkId', 1),
          basePath: getUrlParam('cachePath', `${process.env.PUBLIC_URL}/cache/`),
        }),
        sourceType: 'archive',
      };

    case 'wizard': {
      // Read config from localStorage (set by project-wizard.html "Preview in UI")
      let config = {};
      try {
        const stored = localStorage.getItem('wizardPreviewConfig');
        if (stored) config = JSON.parse(stored);
      } catch (e) {
        console.warn('[providerFactory] Failed to read wizard config from localStorage:', e);
      }
      const details = config.details || {};
      return {
        dataProvider: new WizardProvider({
          projectName: details.projectName || getUrlParam('projectName', 'Preview Project'),
          artistName: details.artistName || getUrlParam('artistName', 'Artist'),
          description: details.projectDescription || getUrlParam('description', ''),
          maxEditions: parseInt(details.maxEditions || getUrlParam('maxEditions', '10'), 10),
          maxGridDimension: parseInt(getUrlParam('maxGridDimension', '4'), 10),
          scriptType: details.scriptType || getUrlParam('scriptType', 'p5js'),
          script: config.script || getUrlParam('script', ''),
          css: config.css || null,
          infoPost: config.info || '',
        }),
        sourceType: 'wizard',
      };
    }

    case 'blockchain':
    default:
      return {
        dataProvider: new BlockchainProvider(),
        sourceType: 'blockchain',
      };
  }
}

function getDataSource() {
  // Check URL params first
  const urlSource = getUrlParam('source');
  if (urlSource) return urlSource;

  // Check env var
  if (process.env.REACT_APP_DATA_SOURCE) {
    return process.env.REACT_APP_DATA_SOURCE;
  }

  return 'blockchain';
}

function getUrlParam(name, defaultValue) {
  if (typeof window === 'undefined') return defaultValue;
  const params = new URLSearchParams(window.location.search);
  return params.get(name) || defaultValue;
}

export default createDataProvider;
